import { Link, Outlet, useNavigate } from "@tanstack/react-router";

import { useCurrentOrganization, useSession } from "../auth/session";
import { Badge, Button } from "../ui/primitives";

export function AppLayout() {
  const session = useSession();
  const navigate = useNavigate();
  const organization = useCurrentOrganization();

  return (
    <div className="min-h-dvh">
      {session.impersonating && <ImpersonationBanner email={session.impersonating} />}

      <header className="border-b border-line bg-raised">
        <div className="mx-auto flex max-w-6xl items-center gap-4 px-6 py-3">
          <Link to="/sites" className="font-semibold">
            Bozor Bot
          </Link>

          <nav aria-label="Main" className="flex items-center gap-1 text-sm">
            <NavLink to="/sites">Sites</NavLink>
            <NavLink to="/billing">Billing</NavLink>
            <NavLink to="/privacy">Privacy</NavLink>
            {session.user?.is_superuser && <NavLink to="/admin">Admin</NavLink>}
          </nav>

          <div className="ml-auto flex items-center gap-3 text-sm">
            {organization && <span className="text-muted">{organization.organization.name}</span>}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                session.signOut();
                void navigate({ to: "/login" });
              }}
            >
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main id="main" className="mx-auto max-w-6xl px-6 py-6">
        <Outlet />
      </main>
    </div>
  );
}

function NavLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <Link
      to={to}
      className="rounded-lg px-2.5 py-1.5 text-muted hover:bg-surface hover:text-ink"
      // Rendered by the router on the active route, so the current page is
      // conveyed to assistive technology and not only by colour.
      activeProps={{ className: "rounded-lg px-2.5 py-1.5 bg-surface text-ink font-medium" }}
      activeOptions={{ exact: to === "/sites" }}
    >
      {children}
    </Link>
  );
}

/**
 * The impersonation warning.
 *
 * Impossible to miss on purpose. The failure this prevents is a staff member
 * forgetting which account they are in and making a change to a customer's
 * live site believing it is their own test store.
 */
function ImpersonationBanner({ email }: { email: string }) {
  return (
    <div role="alert" className="bg-warn/15 px-6 py-2 text-center text-sm text-ink">
      <Badge tone="warn">Impersonating</Badge>{" "}
      <span className="ml-1">
        You are viewing the dashboard as <strong>{email}</strong>. Close this tab to end it.
      </span>
    </div>
  );
}
